import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

import ThemeSettings from './components/ThemeSettings';

const INSPIRATIONAL_QUOTES = [
  "“A reader lives a thousand lives before he dies.” – George R.R. Martin",
  "“The more that you read, the more things you will know. The more that you learn, the more places you'll go.” – Dr. Seuss",
  "“Reading is to the mind what exercise is to the body.” – Richard Steele",
  "“There is no friend as loyal as a book.” – Ernest Hemingway",
  "“To read without reflecting is like eating without digesting.” – Edmund Burke",
  "“Books are a uniquely portable magic.” – Stephen King",
  "“A room without books is like a body without a soul.” – Marcus Tullius Cicero",
  "“Good friends, good books, and a sleepy conscience: this is the ideal life.” – Mark Twain",
  "“We read to know we're not alone.” – William Nicholson",
  "“Fairy tales are more than true: not because they tell us that dragons exist, but because they tell us that dragons can be beaten.” – Neil Gaiman",
  "“If you only read the books that everyone else is reading, you can only think what everyone else is thinking.” – Haruki Murakami",
  "“I have always imagined that Paradise will be a kind of library.” – Jorge Luis Borges",
  "“Read, read, read. Read everything -- trash, classics, good and bad, and see how they do it.” – William Faulkner",
  "“The reading of all good books is like conversation with the finest men of past centuries.” – René Descartes",
  "“Think before you speak. Read before you think.” – Fran Lebowitz",
  "“Today a reader, tomorrow a leader.” – Margaret Fuller",
  "“A book is a dream that you hold in your hand.” – Neil Gaiman",
  "“Show me a family of readers, and I will show you the people who move the world.” – Napoleon Bonaparte",
  "“Sleep is good, he said, and books are better.” – George R.R. Martin",
  "“Books are mirrors: you only see in them what you already have inside you.” – Carlos Ruiz Zafón",
  "“You can never get a cup of tea large enough or a book long enough to suit me.” – C.S. Lewis",
  "“Let us remember: One book, one pen, one child, and one teacher can change the world.” – Malala Yousafzai",
  "“There is no such thing as a child who hates to read; there are only children who have not found the right book.” – Frank Serafini",
  "“A book is a version of the world. If you do not like it, ignore it; or offer your own version in return.” – Salman Rushdie",
  "“That’s the thing about books. They let you travel without moving your feet.” – Jhumpa Lahiri",
  "“Books serve to show a man that those original thoughts of his aren't very new after all.” – Abraham Lincoln",
  "“Read not to contradict and confute; nor to believe and take for granted; nor to find talk and discourse; but to weigh and consider.” – Francis Bacon",
  "“Books break the shackles of time, proof that humans can work magic.” – Carl Sagan",
  "“If one cannot enjoy reading a book over and over again, there is no use in reading it at all.” – Oscar Wilde",
  "“In the case of good books, the point is not to see how many of them you can get through, but rather how many can get through to you.” – Mortimer J. Adler",
  "“I kept always two books in my pocket, one to read, one to write in.” – Robert Louis Stevenson",
  "“Make it a rule never to give a child a book you would not read yourself.” – George Bernard Shaw",
  "“Reading brings us unknown friends.” – Honoré de Balzac",
  "“There is more treasure in books than in all the pirate's loot on Treasure Island.” – Walt Disney",
  "“No two persons ever read the same book.” – Edmund Wilson",
  "“I can survive well enough on my own—if I have the proper reading material.” – Sarah J. Maas",
  "“Reading one book is like eating one potato chip.” – Diane Duane",
  "“Many people, myself among them, feel better at the mere sight of a book.” – Jane Smiley",
  "“You know you've read a good book when you turn the last page and feel a little as if you have lost a friend.” – Paul Sweeney",
  "“Whenever you read a good book, somewhere in the world a door opens to allow in more light.” – Vera Nazarian",
  "“I took a speed-reading course and read War and Peace in twenty minutes. It involves Russia.” – Woody Allen",
  "“A word after a word after a word is power.” – Margaret Atwood",
  "“A book is a gift you can open again and again.” – Garrison Keillor",
  "“The books that the world calls immoral are books that show the world its own shame.” – Oscar Wilde",
  "“Some books leave us free and some books make us free.” – Ralph Waldo Emerson",
  "“Reading makes immigrants of us all. It takes us away from home, but more important, it finds homes for us everywhere.” – Jean Rhys",
  "“A great book should leave you with many experiences, and slightly exhausted at the end.” – William Styron",
  "“We lose ourselves in books, we find ourselves there too.” – Anonymous",
  "“If a book is well written, I always find it too short.” – Jane Austen"
];

// Add jsPDF for PDF export
const loadJsPDF = async () => {
  const { jsPDF } = await import('jspdf');
  return jsPDF;
};

// Helper functions moved outside component
const calculateReadingLevel = (text, words, sentences) => {
  if (words === 0 || sentences === 0) return { label: '', score: 0 };
  
  // Calculate Flesch-Kincaid Grade Level
  const wordsPerSentence = words / sentences;
  const syllables = text.toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/[^aeiou]/g, '')
    .length;
  const syllablesPerWord = syllables / words;
  
  const gradeLevel = (0.39 * wordsPerSentence) + (11.8 * syllablesPerWord) - 15.59;
  const roundedGrade = Math.round(gradeLevel * 10) / 10; // Round to 1 decimal place
  
  let label;
  if (gradeLevel <= 6) label = 'Easy to read';
  else if (gradeLevel <= 12) label = 'Moderate difficulty';
  else label = 'Advanced reading level';

  return { label, score: roundedGrade };
};

const generateSuggestions = (text) => {
  const suggestions = [];
  
  // Check for long sentences (more than 25 words)
  const sentences = text.split(/[.!?]+/).filter(s => s.trim());
  sentences.forEach((sentence, index) => {
    const wordCount = sentence.trim().split(/\s+/).length;
    if (wordCount > 25) {
      suggestions.push(`Consider breaking down sentence ${index + 1} into smaller parts for better readability.`);
    }
  });

  // Check for paragraph length
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());
  paragraphs.forEach((paragraph, index) => {
    const wordCount = paragraph.trim().split(/\s+/).length;
    if (wordCount > 100) {
      suggestions.push(`Consider splitting paragraph ${index + 1} for better readability.`);
    }
  });

  // Check for passive voice (simple check)
  const passiveVoicePattern = /\b(am|is|are|was|were|be|been|being)\s+\w+ed\b/gi;
  const passiveMatches = text.match(passiveVoicePattern);
  if (passiveMatches && passiveMatches.length > 0) {
    suggestions.push('Consider using active voice for clearer communication.');
  }

  return suggestions;
};

function AppContent() {
  const [text, setText] = useState(() => {
    const savedText = localStorage.getItem('readometer-text');
    return savedText || '';
  });
  const [copiedStat, setCopiedStat] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [quote, setQuote] = useState(() => INSPIRATIONAL_QUOTES[Math.floor(Math.random() * INSPIRATIONAL_QUOTES.length)]);
  const [stats, setStats] = useState({
    words: 0,
    characters: 0,
    charactersNoSpaces: 0,
    sentences: 0,
    paragraphs: 0,
    readingTime: {
      rounded: 0,
      minutes: 0,
      seconds: 0
    },
    readingLevel: '',
    gradeLevel: 0,
    suggestions: [],
    wordFrequency: {},
    topWords: [],
  });
  const { colorScheme, theme, toggleTheme } = useTheme();
  const [lastSaved, setLastSaved] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);

  // Memoize calculateStats to prevent infinite loops
  const calculateStats = useCallback((input) => {
    if (!input) {
      setStats({
        words: 0,
        characters: 0,
        charactersNoSpaces: 0,
        sentences: 0,
        paragraphs: 0,
        readingTime: {
          rounded: 0,
          minutes: 0,
          seconds: 0
        },
        readingLevel: '',
        gradeLevel: 0,
        suggestions: [],
        wordFrequency: {},
        topWords: [],
      });
      return;
    }

    const words = input.trim().split(/\s+/).filter(word => word.length > 0);
    const characters = input.length;
    const charactersNoSpaces = input.replace(/\s/g, '').length;
    const sentences = input.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
    const paragraphs = input.split(/\n\s*\n/).filter(para => para.trim().length > 0);
    
    // Calculate word frequency
    const wordFrequency = {};
    const wordsForFrequency = words.map(word => word.toLowerCase().replace(/[.,!?;:'"]/g, ''));
    wordsForFrequency.forEach(word => {
      if (word.length > 2) { // Only count words longer than 2 characters
        wordFrequency[word] = (wordFrequency[word] || 0) + 1;
      }
    });

    // Get top 5 most frequent words
    const topWords = Object.entries(wordFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([word, count]) => ({ word, count }));
    
    // Calculate exact reading time (200 words per minute)
    const exactMinutes = words.length / 200;
    const minutes = Math.floor(exactMinutes);
    const seconds = Math.round((exactMinutes - minutes) * 60);
    const readingTime = {
      rounded: Math.ceil(exactMinutes),
      minutes,
      seconds
    };

    const { label: readingLevel, score: gradeLevel } = calculateReadingLevel(input, words.length, sentences.length);
    const suggestions = generateSuggestions(input);

    setStats({
      words: words.length,
      characters,
      charactersNoSpaces,
      sentences: sentences.length,
      paragraphs: paragraphs.length,
      readingTime,
      readingLevel,
      gradeLevel,
      suggestions,
      wordFrequency,
      topWords,
    });
  }, []); // No dependencies needed since helper functions are now outside

  // Calculate initial stats when component mounts or text changes
  useEffect(() => {
    calculateStats(text);
  }, [text, calculateStats]);

  const handleInputChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    calculateStats(newText);
    if (!newText.trim()) {
      setQuote(INSPIRATIONAL_QUOTES[Math.floor(Math.random() * INSPIRATIONAL_QUOTES.length)]);
    }
  };

  const getGradeLevelColor = (grade) => {
    if (grade === 0) return 'text-gray-400';
    if (grade <= 6) return 'text-green-600';
    if (grade <= 12) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleClear = () => {
    if (text && !window.confirm('Are you sure you want to clear the text? This cannot be undone.')) {
      return;
    }
    setText('');
    setQuote(INSPIRATIONAL_QUOTES[Math.floor(Math.random() * INSPIRATIONAL_QUOTES.length)]);
    setStats({
      words: 0,
      characters: 0,
      charactersNoSpaces: 0,
      sentences: 0,
      paragraphs: 0,
      readingTime: {
        rounded: 0,
        minutes: 0,
        seconds: 0
      },
      readingLevel: '',
      gradeLevel: 0,
      suggestions: [],
      wordFrequency: {},
      topWords: [],
    });
  };

  const handleCopyStat = (statName, value) => {
    let textToCopy = '';
    switch(statName) {
      case 'words':
        textToCopy = `${value} words`;
        break;
      case 'characters':
        textToCopy = `${value} characters`;
        break;
      case 'sentences':
        textToCopy = `${value} sentences`;
        break;
      case 'paragraphs':
        textToCopy = `${value} paragraphs`;
        break;
      case 'readingTime':
        textToCopy = `Reading time: ${value.rounded} min (${value.minutes} min ${value.seconds} sec)`;
        break;
      case 'readingLevel':
        textToCopy = `Reading level: ${value} (Grade ${stats.gradeLevel})`;
        break;
      default:
        textToCopy = value.toString();
        break;
    }
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedStat(statName);
      setTimeout(() => setCopiedStat(null), 2000);
    });
  };



  // Autosave text to localStorage
  useEffect(() => {
    if (text) {
      localStorage.setItem('readometer-text', text);
      setLastSaved(new Date());
    } else {
      localStorage.removeItem('readometer-text');
      setLastSaved(null);
    }
  }, [text]);

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyboard = (e) => {
      // Check if target is textarea to avoid interfering with browser shortcuts
      if (e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl/Cmd + V to focus textarea
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        document.querySelector('textarea').focus();
      }
      
      // Ctrl/Cmd + C to copy all stats
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        const statsText = `
Text Statistics:
- Words: ${stats.words}
- Characters: ${stats.characters} (${stats.charactersNoSpaces} without spaces)
- Sentences: ${stats.sentences}
- Paragraphs: ${stats.paragraphs}
- Reading Time: ${stats.readingTime.rounded} min (${stats.readingTime.minutes} min ${stats.readingTime.seconds} sec)
- Reading Level: ${stats.readingLevel} (Grade ${stats.gradeLevel})
        `.trim();
        navigator.clipboard.writeText(statsText);
        setCopiedStat('all');
        setTimeout(() => setCopiedStat(null), 2000);
      }

      // Escape to clear selection
      if (e.key === 'Escape') {
        document.querySelector('textarea').blur();
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [stats]);

  // Check for shared stats in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('words')) {
      setStats({
        words: parseInt(params.get('words')),
        characters: parseInt(params.get('chars')),
        sentences: parseInt(params.get('sentences')),
        paragraphs: parseInt(params.get('paragraphs')),
        readingTime: {
          rounded: parseInt(params.get('readingTime')),
          minutes: Math.floor(parseInt(params.get('readingTime'))),
          seconds: Math.round((parseInt(params.get('readingTime')) % 1) * 60)
        },
        readingLevel: params.get('readingLevel'),
        gradeLevel: parseFloat(params.get('grade')),
        suggestions: [],
        wordFrequency: {},
        topWords: [],
      });
    }
  }, []);

  // Handle PWA and Service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js').catch(err => {
          console.log('ServiceWorker registration failed: ', err);
        });
      });
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  return (
    <div className={`min-h-screen ${colorScheme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Support floating button */}
        <a 
          href="https://buymeacoffee.com/thariqueazeez" 
          target="_blank" 
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 glass-panel px-4 py-3 rounded-full flex items-center space-x-2 text-gray-800 dark:text-white hover:scale-105 transition-transform duration-200 shadow-lg"
        >
          <span>☕</span>
          <span className="font-medium text-sm hidden sm:block">Support the Creator</span>
        </a>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Bar */}
          <nav className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center">
              <img src="/logo.png" alt="Read-o-Meter" className="h-16" />
            </div>
            <div className="flex items-center space-x-2">
              {isInstallable && (
                <button
                  onClick={handleInstallClick}
                  className="px-3 py-1 bg-indigo-600 text-white rounded font-medium text-sm hover:bg-indigo-700 transition-colors hidden sm:block"
                >
                  Install App
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Theme Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </button>
            </div>
          </nav>
          
          {/* Settings Panel */}
          {showSettings && (
            <div className="mb-6">
              <ThemeSettings />
            </div>
          )}

          {/* Main Content */}
          <div className="glass-panel rounded-2xl p-6 mb-6 animate-fade-in">
            <div className="flex justify-end mb-2">
              <button
                onClick={handleClear}
                className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                Clear text
              </button>
            </div>
          
            {/* Prominent Reading Time Display or Quote */}
            {!text ? (
              <div 
                className="text-center mb-8 animate-fade-in p-8 bg-[#fdfbf7] dark:bg-gray-800/80 rounded-xl shadow-sm border border-[#e8e4d9] dark:border-gray-700/50"
                style={{ fontFamily: "'EB Garamond', 'Crimson Text', serif" }}
              >
                <p className="text-2xl italic text-[#4a4a4a] dark:text-gray-300 leading-relaxed tracking-wide">
                  {quote}
                </p>
              </div>
            ) : stats.words > 0 && (
              <div 
                className="text-center mb-6 animate-fade-in relative group cursor-pointer"
                onClick={() => handleCopyStat('readingTime', stats.readingTime)}
                title="Click to copy reading time"
              >
                <p className="text-gray-500 dark:text-gray-400 text-lg mb-1">Estimated Reading Time</p>
                <div className="relative inline-block">
                  <h2 className="text-5xl font-bold text-indigo-600 dark:text-indigo-400 inline-block">
                    {stats.readingTime.minutes} min {stats.readingTime.seconds} sec
                  </h2>
                  <span className={`absolute -right-12 top-0 transition-all duration-200 ${copiedStat === 'readingTime' ? 'opacity-0 scale-90' : 'opacity-0 group-hover:opacity-100 scale-100'}`}>
                    <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </span>
                  {copiedStat === 'readingTime' && (
                    <span className="absolute -right-20 top-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-md transform scale-100 transition-all duration-200">
                      Copied!
                    </span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                  <span>{stats.words} words</span>
                  <span className="hidden sm:inline">•</span>
                  <span>{stats.characters} chars</span>
                  <span className="hidden sm:inline">•</span>
                  <span>{stats.sentences} sentences</span>
                  {stats.readingLevel && (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <span>Level: {stats.readingLevel}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            <textarea
              className="w-full h-64 p-5 rounded-xl border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 resize-y shadow-inner text-base bg-white dark:bg-gray-800"
              placeholder="Paste or type your text here to get instant analysis..."
              value={text}
              onChange={handleInputChange}
            />
            <div className="mt-3 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>100% Free • No registration needed</span>
            </div>
          </div>

          <div className="mt-12 mb-8 text-center space-y-4 text-xs text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            <p>
              **Calculation is based on the <a href="http://ezinearticles.com/?What-is-the-Average-Reading-Speed-and-the-Best-Rate-of-Reading?&id=2298503" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">average reading speed</a> that around 200 words per minute (wpm).
            </p>
            <p>
              Your privacy matters. All text is processed locally on your device and is never uploaded or saved to any server.
            </p>
            <p className="pt-4 border-t border-gray-200 dark:border-gray-800">
              © Read-o-Meter 2010-{new Date().getFullYear()} : Created by <a href="http://twitter.com/ThariqueAzeez" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">Tharique Azeez</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App; 